import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, DollarSign, Calendar, FileSpreadsheet, Download, Printer, BarChart3, ChevronDown, ChevronUp, Check, History, Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { useApp } from '@/contexts/AppContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CashierClosingReportDialog from '@/components/cashier/CashierClosingReportDialog';
import OrderReprintButton from '@/components/orders/OrderReprintButton';
import { printCashierClosingReport } from '@/services/printService';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const CashierClosingPage = () => {
  const { paymentMethods, orders } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [date, setDate] = useState<DateRange | undefined>({
    from: new Date(),
    to: new Date(),
  });

  const completedOrders = orders.filter(order => order.status === 'paid');

  const paymentSummary = completedOrders.reduce((acc, order) => {
    const methodId = order.paymentMethodId || 'other';
    if (!acc[methodId]) {
      acc[methodId] = {
        count: 0,
        total: 0,
        method: paymentMethods.find(m => m.id === methodId) || { id: methodId, name: 'Outro' }
      };
    }
    acc[methodId].count += 1;
    acc[methodId].total += order.totalAmount;
    return acc;
  }, {} as Record<string, { count: number, total: number, method: any }>);

  const totalValue = Object.values(paymentSummary).reduce((sum, item) => sum + item.total, 0);

  const summaryItems = Object.values(paymentSummary).sort((a, b) => b.total - a.total);

  const cashierData = {
    operator: localStorage.getItem('current_user_name') || 'Operador',
    openDate: new Date().setHours(0, 0, 0, 0),
    closeDate: new Date(),
    totalIn: totalValue,
    totalOut: 0,
    expectedAmount: totalValue
  };

  const salesData = {
    totalOrders: completedOrders.length,
    totalAmount: totalValue,
    averageTicket: completedOrders.length > 0 ? totalValue / completedOrders.length : 0,
    canceledOrders: 0,
    canceledAmount: 0
  };

  const paymentsData = summaryItems.map(item => ({
    name: item.method.name,
    count: item.count,
    amount: item.total
  }));

  const handleCloseRegister = async () => {
    setIsLoading(true);
    
    try {
      setReportDialogOpen(true);
      
      setTimeout(() => {
        toast({
          title: "Caixa fechado com sucesso",
          description: `Fechamento realizado para ${format(new Date(), 'dd/MM/yyyy')}`,
        });
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error("Erro ao fechar caixa:", error);
      setIsLoading(false);
      toast({
        title: "Erro ao fechar caixa",
        description: "Ocorreu um erro ao processar o fechamento",
        variant: "destructive"
      });
    }
  };

  const handleExport = () => {
    toast({
      title: "Exportando dados",
      description: "Os dados estão sendo exportados para Excel",
    });
  };

  const handlePrint = async () => {
    try {
      await printCashierClosingReport(cashierData, salesData, paymentsData);
      
      toast({
        title: "Relatório enviado para impressão",
        description: "O relatório de fechamento foi enviado para a impressora",
      });
    } catch (error) {
      console.error("Erro ao imprimir:", error);
      toast({
        title: "Erro ao imprimir",
        description: "Não foi possível enviar para impressão. Verifique a configuração da impressora.",
        variant: "destructive"
      });
    }
  };

  const handlePrintReceipt = async () => {
    localStorage.setItem('use_thermal_receipt_format', 'true');
    handlePrint();
  };

  const handlePrintA4 = async () => {
    localStorage.setItem('use_thermal_receipt_format', 'false');
    handlePrint();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Fechamento de Caixa</h1>
        <p className="text-muted-foreground">
          Visualize e gerencie o fechamento diário do seu caixa
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-800">
              <Calendar className="h-5 w-5 mr-2" />
              Período
            </CardTitle>
            <CardDescription>
              Selecione o período para o fechamento
            </CardDescription>
          </CardHeader>
          <CardContent>
            {date ? (
              <div className="grid gap-2">
                <DatePickerWithRange className="[&>button]:border-blue-200 [&>button:hover]:bg-blue-100" date={date} onDateChange={setDate} />
              </div>
            ) : (
              <div className="border rounded-md p-2 text-center text-sm text-muted-foreground">
                Selecione um período para visualizar
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center text-green-800">
              <BarChart3 className="h-5 w-5 mr-2" />
              Resumo do Fechamento
            </CardTitle>
            <CardDescription>
              Resumo financeiro do período selecionado
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total Vendas:</span>
              <span className="text-xl font-bold text-green-700">{formatCurrency(totalValue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Quantidade de Pedidos:</span>
              <Badge variant="outline" className="bg-green-100">{completedOrders.length}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="grid grid-cols-2 mb-4">
          <TabsTrigger value="summary">Resumo por Forma de Pagamento</TabsTrigger>
          <TabsTrigger value="orders">Lista de Pedidos</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-0">
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent">
              <CardTitle>Formas de Pagamento</CardTitle>
              <CardDescription>
                Detalhamento dos valores por forma de pagamento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-4">
                  {summaryItems.length > 0 ? (
                    summaryItems.map((item) => (
                      <div 
                        key={item.method.id} 
                        className="flex items-center justify-between p-3 rounded-md border bg-gradient-to-r from-gray-50 to-transparent"
                      >
                        <div className="flex items-center gap-3">
                          {item.method.name.toLowerCase().includes('cartão') || 
                           item.method.name.toLowerCase().includes('credito') || 
                           item.method.name.toLowerCase().includes('debito') ? (
                            <CreditCard className="h-5 w-5 text-purple-600" />
                          ) : (
                            <DollarSign className="h-5 w-5 text-green-600" />
                          )}
                          <div>
                            <div className="font-medium">{item.method.name}</div>
                            <div className="text-sm text-muted-foreground">{item.count} transações</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold">{formatCurrency(item.total)}</div>
                          <div className="text-xs text-muted-foreground">
                            {((item.total / totalValue) * 100).toFixed(1)}% do total
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhum pagamento encontrado no período selecionado
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
            <CardFooter className="flex justify-between border-t p-4 bg-gradient-to-r from-slate-50 to-transparent">
              <div className="text-sm text-muted-foreground">
                Total: <span className="font-bold">{formatCurrency(totalValue)}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-1" />
                  Exportar
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Printer className="h-4 w-4 mr-1" />
                      Imprimir
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handlePrintReceipt}>
                      <Receipt className="h-4 w-4 mr-2" />
                      Imprimir como Cupom
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePrintA4}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Imprimir em A4
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="orders" className="mt-0">
          <Card>
            <CardHeader className="bg-gradient-to-r from-amber-50 to-transparent">
              <CardTitle>Lista de Pedidos</CardTitle>
              <CardDescription>
                Todos os pedidos incluídos neste fechamento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {completedOrders.length > 0 ? (
                    completedOrders.map((order) => {
                      const method = paymentMethods.find(m => m.id === order.paymentMethodId);
                      return (
                        <div 
                          key={order.id} 
                          className="p-3 rounded-md border bg-gradient-to-r from-gray-50 to-transparent"
                        >
                          <div className="flex justify-between">
                            <div>
                              <div className="font-medium">Pedido #{order.id.substring(0, 8)}</div>
                              <div className="text-sm text-muted-foreground">
                                {order.type === 'dine-in' ? `Mesa ${order.tableId?.split('-')[0] || 'N/A'}` : 'Balcão/Delivery'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold">{formatCurrency(order.totalAmount)}</div>
                              <Badge variant="outline" className="bg-purple-50 text-xs">
                                {method?.name || 'N/A'}
                              </Badge>
                            </div>
                          </div>
                          <div className="mt-2 flex justify-end">
                            <OrderReprintButton order={order} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhum pedido encontrado no período selecionado
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end mt-6">
        <Button 
          className="bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 gap-2"
          onClick={handleCloseRegister}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <span className="animate-spin">
                <BarChart3 className="h-4 w-4" />
              </span>
              <span>Processando...</span>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              <span>Fechar Caixa</span>
            </>
          )}
        </Button>
      </div>
      
      <CashierClosingReportDialog 
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        cashierData={cashierData}
        salesData={salesData}
        paymentsData={paymentsData}
      />
    </div>
  );
};

export default CashierClosingPage;
